import type { ScanDto, ValuationResponse } from '@compflash/contracts';
import { describeCondition, describeIdentity, formatDays, formatMoney, formatPercent } from '../lib/format';
import { PriceHistoryChart } from './PriceHistoryChart';
import { CompsTable } from './CompsTable';

type Valuation = NonNullable<ScanDto['valuation']>;
type Recommendation = NonNullable<ScanDto['recommendation']>;

export interface ResultPanelProps {
  identity: Record<string, unknown> | null;
  condition: ScanDto['identification'] extends null ? never : { kind: string } | null;
  identityConfidence: number | null;
  abstained: boolean;
  abstentionReason: string | null;
  valuation: Valuation | null;
  recommendation: Recommendation | null;
  timings?: ScanDto['timings'] | null;
  stage?: ScanDto['stage'];
  onCorrect?: () => void;
  onSave?: () => void;
  onWatch?: () => void;
}

const VERDICT_COPY: Record<string, { label: string; tone: string; blurb: string }> = {
  buy: { label: 'Buy', tone: 'buy', blurb: 'The current all-in cost is comfortably below the ceiling.' },
  fair: { label: 'Fair price', tone: 'fair', blurb: 'The price is close to what the comps support.' },
  avoid: { label: 'Avoid', tone: 'avoid', blurb: 'The current all-in cost is above the ceiling.' },
  awaiting_price: { label: 'Awaiting price', tone: 'neutral', blurb: 'Enter the current auction price for a verdict.' },
  insufficient_data: { label: 'Not enough data', tone: 'neutral', blurb: 'There is not enough evidence to advise a bid.' },
};

export function ResultPanel(props: ResultPanelProps): JSX.Element {
  const { valuation, recommendation, abstained } = props;

  if (abstained) {
    return (
      <section className="panel abstain-panel">
        <h2>Unable to identify this card reliably</h2>
        <p className="muted">{props.abstentionReason ?? 'The image did not give enough evidence to name a specific card.'}</p>
        <ul className="muted small">
          <li>Wait until the card is held still and fills more of the frame.</li>
          <li>Reduce glare from the toploader or slab by changing the angle.</li>
          <li>Or enter the card manually from the Lookup page.</li>
        </ul>
        <p className="disclaimer">Showing nothing is deliberate: a confident wrong identification is worse than no answer.</p>
      </section>
    );
  }

  const verdict = recommendation ? (VERDICT_COPY[recommendation.verdict] ?? VERDICT_COPY['insufficient_data']!) : null;

  return (
    <section className="panel result-panel">
      <header className="result-head">
        <div>
          <h2>{describeIdentity(props.identity)}</h2>
          <div className="chips">
            <span className="chip">{describeCondition(props.condition as never)}</span>
            {props.identityConfidence !== null && (
              <span className={`chip ${props.identityConfidence >= 0.8 ? 'good' : props.identityConfidence >= 0.55 ? 'warn' : 'bad'}`}>
                {Math.round(props.identityConfidence * 100)}% match
              </span>
            )}
            {valuation && <span className="chip">{valuation.usedCount} comps used</span>}
            {valuation && (
              <span className={`chip ${valuation.freshness.label === 'fresh' || valuation.freshness.label === 'recent' ? 'good' : 'warn'}`}>
                newest {formatDays(valuation.freshness.newestAgeDays)}
              </span>
            )}
          </div>
        </div>
        <div className="result-actions">
          {props.onSave && (
            <button type="button" className="ghost" onClick={props.onSave}>
              Save
            </button>
          )}
          {props.onWatch && (
            <button type="button" className="ghost" onClick={props.onWatch}>
              Watch
            </button>
          )}
          {props.onCorrect && (
            <button type="button" className="ghost" onClick={props.onCorrect}>
              Not this card?
            </button>
          )}
        </div>
      </header>

      {verdict && recommendation && (
        <div className={`verdict verdict-${verdict.tone}`}>
          <div>
            <div className="verdict-label">{verdict.label}</div>
            <div className="verdict-blurb">{verdict.blurb}</div>
          </div>
          <div className="verdict-numbers">
            <div>
              <span className="label">Max bid</span>
              <strong>{formatMoney(recommendation.maxBid)}</strong>
            </div>
            <div>
              <span className="label">Max all-in</span>
              <strong>{formatMoney(recommendation.maxAllIn)}</strong>
            </div>
            <div>
              <span className="label">Confidence</span>
              <strong>{Math.round(recommendation.confidence * 100)}%</strong>
            </div>
          </div>
        </div>
      )}

      {valuation && (
        <div className="stat-grid">
          <Stat label="Fair value" value={formatMoney(valuation.fairValue)} hint={valuation.fairValueBasis.replace(/_/g, ' ')} />
          <Stat label="Last sale" value={formatMoney(valuation.lastSale?.total ?? null)} hint={formatDays(valuation.freshness.newestAgeDays)} />
          <Stat label="Median" value={formatMoney(valuation.median)} />
          <Stat
            label="Range (p10–p90)"
            value={valuation.range ? `${formatMoney(valuation.range.p10)} – ${formatMoney(valuation.range.p90)}` : '—'}
          />
          <Stat
            label="Trend / 30d"
            value={formatPercent(valuation.trend.changePer30Days)}
            tone={valuation.trend.direction === 'up' ? 'good' : valuation.trend.direction === 'down' ? 'bad' : undefined}
            hint={valuation.trend.method.replace(/_/g, ' ')}
          />
          <Stat label="Liquidity" value={valuation.liquidity.label} hint={`${valuation.liquidity.salesLast30Days} sales / 30d`} />
          <Stat label="Volatility" value={valuation.volatility.label} hint={`rel. MAD ${valuation.volatility.relativeMad ?? '—'}`} />
          <Stat label="Data confidence" value={`${Math.round(valuation.confidence * 100)}%`} />
        </div>
      )}

      {valuation && valuation.comps.length > 0 && (
        <>
          <h3>Price history</h3>
          <PriceHistoryChart valuation={valuation} />
        </>
      )}

      {recommendation && (
        <>
          <h3>Why</h3>
          <ul className="factors">
            {recommendation.factors.map((factor) => (
              <li key={factor.key} className={`factor factor-${factor.impact}`}>
                <span className="factor-label">{factor.label}</span>
                <span className="factor-detail">{factor.detail}</span>
              </li>
            ))}
          </ul>

          {recommendation.missingInformation.length > 0 && (
            <div className="notice notice-warn">
              <strong>Missing information</strong>
              <ul>
                {recommendation.missingInformation.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {recommendation.warnings.length > 0 && (
            <div className="notice notice-warn">
              <strong>Caveats</strong>
              <ul>
                {recommendation.warnings.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          <details className="calc">
            <summary>Show the calculation</summary>
            <table className="calc-table">
              <tbody>
                {recommendation.calculation.map((step) => (
                  <tr key={`${step.step}-${step.expression}`}>
                    <th>{step.step.replace(/_/g, ' ')}</th>
                    <td className="mono">{step.expression}</td>
                    <td className="mono strong">{step.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </>
      )}

      {valuation && valuation.comps.length > 0 && (
        <>
          <h3>Comparable sales</h3>
          <CompsTable valuation={valuation} />
        </>
      )}

      {valuation && valuation.dataSources.length > 0 && (
        <p className="muted small">
          Sources:{' '}
          {valuation.dataSources.map((source) => `${source.provider} (${source.count}, ${source.licence})`).join(' · ')}
        </p>
      )}

      {props.timings?.totalMs !== undefined && props.timings?.totalMs !== null && (
        <p className="muted small">
          Completed in {Math.round(props.timings.totalMs)} ms
          {props.timings.compsCacheHit ? ' using cached comps' : ''}
          {props.timings.identificationMs !== null ? ` · identification ${Math.round(props.timings.identificationMs)} ms` : ''}
          {props.timings.compsMs !== null ? ` · comps ${Math.round(props.timings.compsMs)} ms` : ''}
        </p>
      )}

      {recommendation && <p className="disclaimer">{recommendation.disclaimers.join(' ')}</p>}
    </section>
  );
}

function Stat({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: 'good' | 'bad' }): JSX.Element {
  return (
    <div className="stat">
      <span className="label">{label}</span>
      <strong className={tone ? `tone-${tone}` : undefined}>{value}</strong>
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

export function valuationResponseToPanelProps(response: ValuationResponse): ResultPanelProps {
  return {
    identity: response.identity as unknown as Record<string, unknown>,
    condition: response.condition as never,
    identityConfidence: 1,
    abstained: false,
    abstentionReason: null,
    valuation: response.valuation as unknown as Valuation,
    recommendation: response.recommendation as unknown as Recommendation,
    timings: null,
  };
}
